import { useState } from 'react';
import { useWeather, getWeatherInfo, getWindDirectionText } from '../../hooks/useWeather.js';

interface SpotWeatherSectionProps {
  lat: number;
  lng: number;
}

export function SpotWeatherSection({ lat, lng }: SpotWeatherSectionProps) {
  const { weather, loading } = useWeather(lat, lng);
  const [expanded, setExpanded] = useState(false);

  if (!loading && weather === null) return null;

  const formatHour = (isoTime: string) => `${parseInt(isoTime.slice(11, 13), 10)}時`;
  const formatPrecip = (prob: number) => (prob < 5 ? '—' : `${prob}%`);

  const c = weather?.current;

  return (
    <div className="mb-4 rounded-xl border border-gray-200 overflow-hidden">
      {/* ヘッダー兼トグルボタン */}
      <button
        type="button"
        className="w-full px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between active:bg-gray-100"
        onClick={() => weather && setExpanded((v) => !v)}
      >
        <span className="text-xs font-medium text-gray-500">現地の天気</span>
        {weather && <span className="text-gray-400 text-xs">{expanded ? '▲' : '▼'}</span>}
      </button>

      {loading && !weather ? (
        <div className="px-3 py-2.5 flex items-center gap-2">
          <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse" />
          <div className="space-y-1">
            <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
            <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
      ) : c ? (
        <>
          {/* 現在の天気（常時表示） */}
          <div className="px-3 py-2.5 flex items-center gap-3">
            <span className="text-3xl leading-none">{getWeatherInfo(c.weatherCode).emoji}</span>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {getWeatherInfo(c.weatherCode).label}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {Math.round(c.temperature)}°
                <span className="text-gray-400 ml-1">
                  体感 {Math.round(c.apparentTemperature)}°
                </span>
                <span className="ml-2">
                  {getWindDirectionText(c.windDirection)} {Math.round(c.windSpeed)}km/h
                </span>
              </p>
            </div>
          </div>

          {/* 5時間予報（展開時のみ） */}
          {expanded && weather.hourly.length > 0 && (
            <div className="border-t border-gray-100 divide-y divide-gray-50">
              {weather.hourly.map((h) => {
                const info = getWeatherInfo(h.weatherCode);
                return (
                  <div key={h.time} className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-gray-400 font-medium w-8 shrink-0">
                        {formatHour(h.time)}
                      </span>
                      <span className="text-lg leading-none">{info.emoji}</span>
                      <span className="text-sm font-medium text-gray-700">{info.label}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 pl-[2.375rem] text-xs text-gray-500">
                      <span className="font-semibold text-gray-800">
                        {Math.round(h.temperature)}°
                      </span>
                      <span className="text-gray-400">
                        体感 {Math.round(h.apparentTemperature)}°
                      </span>
                      <span
                        className={
                          h.precipitationProbability >= 50 ? 'text-blue-500 font-medium' : ''
                        }
                      >
                        {formatPrecip(h.precipitationProbability)}
                      </span>
                      <span>
                        {getWindDirectionText(h.windDirection)} {Math.round(h.windSpeed)}km/h
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
